import React, { useState } from 'react';
import { Eye, EyeOff, Monitor, Tablet, Smartphone, Copy, Trash2, Database } from 'lucide-react';
import { Label } from '../../ui/label';
import { Input } from '../../ui/input';
import { FieldEditor, ImageField, Chip } from './FieldEditors';
import { DataSourcePicker } from './DataSourcePicker';

const LAYOUT_THUMB = {
  full_bg: 'M0 0h48v24H0z', split: 'M0 0h22v24H0zM26 0h22v24H26z', centered: 'M12 6h24v12H12z', minimal: 'M4 8h28v3H4zM4 14h20v2H4z',
  image_left: 'M0 0h22v24H0zM26 6h18v3H26zM26 12h14v2H26z', image_right: 'M26 0h22v24H26zM4 6h18v3H4zM4 12h14v2H4z',
  cards: 'M0 0h14v24H0zM17 0h14v24H17zM34 0h14v24H34z', grid: 'M0 0h14v11H0zM17 0h14v11H17zM34 0h14v11H34zM0 13h14v11H0zM17 13h14v11H17zM34 13h14v11H34z',
  list: 'M0 0h48v6H0zM0 9h48v6H0zM0 18h48v6H0z', dark: 'M0 0h48v24H0z', light: 'M0 0h48v24H0z', banner: 'M0 8h48v8H0z',
};

const Thumb = ({ id, active }) => (
  <svg viewBox="0 0 48 24" className={`w-full h-8 ${active ? 'text-amber-600' : 'text-neutral-400'}`}><path d={LAYOUT_THUMB[id] || 'M0 0h48v24H0z'} fill="currentColor" opacity={id === 'light' ? 0.25 : 0.8} /></svg>
);

// Right-hand inspector for the selected section: layout, content, data, style, visibility.
export const SectionInspector = ({ api, section, spec, pages, onChange, onDuplicate, onDelete, onAi, pageName }) => {
  const [tab, setTab] = useState('content');
  if (!section || !spec) return null;
  const set = (patch) => onChange({ ...section, ...patch });
  const setProp = (k, v) => set({ props: { ...(section.props || {}), [k]: v } });
  const setStyle = (k, v) => set({ style: { ...(section.style || {}), [k]: v } });
  const vis = section.visibility || { desktop: true, tablet: true, mobile: true };

  return (
    <div className="h-full flex flex-col" data-testid="section-inspector">
      <div className="px-4 py-3 border-b border-neutral-200 flex items-center justify-between">
        <div><p className="text-xs text-neutral-500 uppercase tracking-wide">{spec.category}</p><h3 className="font-semibold text-neutral-900">{spec.label}</h3></div>
        <div className="flex items-center gap-1">
          <button title={section.hidden ? 'Show section' : 'Hide section'} onClick={() => set({ hidden: !section.hidden })} className="p-1.5 hover:bg-neutral-100" data-testid="inspector-toggle-hidden">{section.hidden ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
          <button title="Duplicate" onClick={onDuplicate} className="p-1.5 hover:bg-neutral-100" data-testid="inspector-duplicate"><Copy className="w-4 h-4" /></button>
          <button title="Delete" onClick={onDelete} className="p-1.5 hover:bg-red-50 text-red-600" data-testid="inspector-delete"><Trash2 className="w-4 h-4" /></button>
        </div>
      </div>
      <div className="flex border-b border-neutral-200 text-sm">
        {['content', spec.source && 'data', 'design'].filter(Boolean).map((t) => (
          <button key={t} onClick={() => setTab(t)} className={`flex-1 py-2 capitalize ${tab === t ? 'border-b-2 border-neutral-900 font-medium' : 'text-neutral-500'}`} data-testid={`inspector-tab-${t}`}>{t === 'data' ? <span className="inline-flex items-center gap-1"><Database className="w-3.5 h-3.5" />Data</span> : t}</button>
        ))}
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {tab === 'content' && (
          <>
            <div>
              <Label className="text-xs">Layout</Label>
              <div className="mt-1 grid grid-cols-2 gap-2">
                {spec.layouts.map((l) => (
                  <button key={l.id} onClick={() => set({ layout: l.id })} data-testid={`layout-${l.id}`}
                    className={`border p-2 text-left text-xs ${section.layout === l.id ? 'border-amber-500 bg-amber-50' : 'border-neutral-200 hover:border-neutral-400'}`}>
                    <Thumb id={l.id} active={section.layout === l.id} /><span className="block mt-1 truncate">{l.label}</span>
                  </button>
                ))}
              </div>
            </div>
            {spec.fields.map((f) => <FieldEditor key={f.key} api={api} field={f} value={section.props?.[f.key]} onChange={(v) => setProp(f.key, v)} pages={pages} onAi={onAi ? (field) => onAi(section, field) : undefined} context={{ pageName, sectionType: section.type, text: [section.props?.eyebrow, section.props?.title, section.props?.body].filter(Boolean).join(' ').replace(/<[^>]+>/g, ' ').slice(0, 600) }} />)}
          </>
        )}
        {tab === 'data' && spec.source && (
          <>
            <p className="text-xs text-neutral-500">Pull live content from your existing catalog so you never enter it twice.</p>
            <DataSourcePicker api={api} spec={spec.source} source={section.source} onChange={(src) => set({ source: src })} />
          </>
        )}
        {tab === 'design' && (
          <>
            <div><Label className="text-xs">Background</Label>
              <div className="mt-1 flex flex-wrap gap-1.5">{['white', 'light', 'dark', 'brand', 'image'].map((b) => <Chip key={b} active={(section.style?.background || 'white') === b} onClick={() => setStyle('background', b)} testId={`style-bg-${b}`}>{b}</Chip>)}</div></div>
            {section.style?.background === 'image' && <ImageField api={api} label="Background image" value={section.style.background_image} onChange={(v) => setStyle('background_image', v)} />}
            <div><Label className="text-xs">Spacing</Label>
              <div className="mt-1 flex flex-wrap gap-1.5">{['none', 'compact', 'normal', 'spacious'].map((p) => <Chip key={p} active={(section.style?.padding || 'normal') === p} onClick={() => setStyle('padding', p)}>{p}</Chip>)}</div></div>
            <div><Label className="text-xs">Text alignment</Label>
              <div className="mt-1 flex gap-1.5">{['left', 'center'].map((a) => <Chip key={a} active={(section.style?.align || 'left') === a} onClick={() => setStyle('align', a)}>{a}</Chip>)}</div></div>
            <div><Label className="text-xs">Show on</Label>
              <div className="mt-1 flex gap-1.5">
                {[['desktop', Monitor], ['tablet', Tablet], ['mobile', Smartphone]].map(([d, I]) => (
                  <button key={d} onClick={() => set({ visibility: { ...vis, [d]: !vis[d] } })} data-testid={`vis-${d}`} className={`flex-1 border py-2 flex items-center justify-center gap-1.5 text-xs capitalize ${vis[d] !== false ? 'border-neutral-900 bg-neutral-900 text-white' : 'border-neutral-300 text-neutral-400'}`}><I className="w-3.5 h-3.5" />{d}</button>
                ))}
              </div></div>
            <div><Label className="text-xs">Anchor ID (optional, for in-page links)</Label><Input className="h-9" value={section.style?.anchor || ''} onChange={(e) => setStyle('anchor', e.target.value)} placeholder="e.g. benefits" /></div>
          </>
        )}
      </div>
    </div>
  );
};
